import { useTranslation } from 'react-i18next'
import {
    UsageCostRequestParams,
    UsageCostLabelsMap,
    UsageCostResponseBody,
} from '../useApi/types'
import { flattenUsageCostAugmentedEntry } from '../../utils/flattenUsageCost'
import { useAnalyticsApi } from '../useApi/useAnalyticsApi/useAnalyticsApi'

export const useUsageAndCostLabelsMap = (
    filters: UsageCostRequestParams,
    units?: boolean,
    data?: UsageCostResponseBody
): UsageCostLabelsMap => {
    const { useUsageCostQuery } = useAnalyticsApi()
    const query = useUsageCostQuery(filters, {
        enabled: !!data,
    })
    const { t } = useTranslation('translations')

    const dataToFlatten = data ?? query.data
    const flattenedData = dataToFlatten?.items[0]
        ? flattenUsageCostAugmentedEntry(dataToFlatten.items[0])
        : undefined

    const withUsageUnit = (label: string) => {
        const unit = flattenedData?.usage_unit

        if (units && unit) {
            return `${label} (${unit})`
        }

        return label
    }

    const withCostUnit = (label: string) => {
        const unit = flattenedData?.cost_unit

        if (units && unit) {
            return `${label} (${unit})`
        }

        return label
    }

    const withDemandUnit = (label: string) => {
        const unit = flattenedData?.demand_unit

        if (units && unit) {
            return `${label} (${unit})`
        }

        return label
    }

    const labelsMap: UsageCostLabelsMap = {
        date: t('usage_cost_table.date'),

        usage: t('usage_cost_table.usage'),
        usage_commodityUsage: withUsageUnit(
            t('usage_cost_table.commodity_usage')
        ),
        usage_transportUsage: withUsageUnit(t('usage_cost_table.transport')),
        usage_baselineUsage: withUsageUnit(
            t('usage_cost_table.baseline_usage')
        ),
        usage_budget: withUsageUnit(t('usage_cost_table.budget')),
        usage_weatherNormalized: withUsageUnit(
            t('usage_cost_table.weather_normalized')
        ),

        cost: t('usage_cost_table.cost'),

        cost_commodity: t('usage_cost_table.total_commodity_cost'),
        cost_commodity_totalCommodityCost: withCostUnit(
            t('usage_cost_table.total_commodity_cost')
        ),
        cost_commodity_supply: withCostUnit(t('usage_cost_table.supply')),
        cost_commodity_transmission: withCostUnit(
            t('usage_cost_table.transmission')
        ),
        cost_commodity_capacity: withCostUnit(t('usage_cost_table.capacity')),

        cost_transport: t('usage_cost_table.total_transport_cost'),
        cost_transport_totalTransportCost: withCostUnit(
            t('usage_cost_table.total_transport_cost')
        ),
        cost_transport_tariff: withCostUnit(t('usage_cost_table.tariffs')),
        cost_transport_4cp4ncp: withCostUnit(t('usage_cost_table.four_cp_ncp')),
        cost_transport_billingDemand: withCostUnit(
            t('usage_cost_table.billing_demand')
        ),
        cost_taxes: withCostUnit(t('usage_cost_table.taxes')),
        cost_totalInvoiced_noUnit: t('usage_cost_table.total_cost'),
        cost_totalInvoiced: withCostUnit(t('usage_cost_table.total_cost')),
        cost_other: withCostUnit(t('usage_cost_table.other')),
        cost_baselineCost: withCostUnit(t('usage_cost_table.baseline_cost')),
        cost_budget: withCostUnit(t('usage_cost_table.budget_cost')),

        demand: t('usage_cost_table.demand'),
        demand_actual: withDemandUnit(t('usage_cost_table.demand_actual')),
        demand_billing: withDemandUnit(t('usage_cost_table.demand_billing')),
    }

    return labelsMap
}
